import { route } from 'quasar/wrappers';
import { AuthService, LoggingService } from 'src/services';
import VueRouter, { Route, NavigationGuardNext, RawLocation } from 'vue-router';
import { routes, RouteNames } from './routes';
import { AppStore } from 'src/store';
import { Loading } from 'quasar';
import { ApiException } from 'src/services/generated/api.generated.v1';

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation
 */

const authService = new AuthService();

const publicPages: Array<string> = [
  RouteNames.Login,
  RouteNames.ForgotPassword,
  RouteNames.ResetPassword,
  RouteNames.Register,
  RouteNames.ConfirmEmail,
  RouteNames.SplashScreen,
  RouteNames.CompanyToggle
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleApiError(err: any, processRoute: () => void) {
  const apiException = err as ApiException;

  if (err || (apiException && apiException.status !== 401)) {
    LoggingService.LogError(err);
  }

  processRoute();
}


function tryUpdateRefreshToken(): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {  
    authService.RefreshToken().then(async (authResult) => {
      AppStore.commit('user/updateUserState', authResult);
      const isAuthenticated = authResult?.succeeded === true;
  
      if (Loading.isActive) {
        Loading.hide();
      }
  
      await AppStore.dispatch('setting/setSettings');
      await AppStore.dispatch('consumerInformation/loadContentLinks');
      resolve(isAuthenticated);
    }).catch((err) => {
      reject(err);
    });  
  });
}


function authGuard(to: Route, from: Route, next: NavigationGuardNext<Vue>) {
  const user = AppStore.state.user;
  const token = AuthService.GetUserToken();
  const isAuthenticated: boolean = token !== null && user.id !== null;

  const isPublicPage = () => (publicPages.indexOf(to.name as string) > -1);

  const processRoute = (authenticatedForRoute: boolean) => {
    if (!isPublicPage() && !authenticatedForRoute) {
      const nextRoute: RawLocation = to.fullPath !== '/'
        ? { path: '/login', query: { redirect: to.fullPath }}
        : { path: '/welcome' };

      next(nextRoute);
    } else if (to.name != RouteNames.AcceptTermsConditions && user && !user.termsAndConditionsAccepted && isAuthenticated) {
      next({ path: '/accept-terms-conditions' })
    } else {
      next();
    }
  }

  if (!isAuthenticated && !isPublicPage()) {
    tryUpdateRefreshToken()
      .then((refreshSuccess) => {
        processRoute(refreshSuccess)
      })
      .catch((err) => {
        handleApiError(err, () => processRoute(false));
      });
  } else {
    processRoute(isAuthenticated);
  }
};

export default route(function ({ Vue }) {
  Vue.use(VueRouter);

  const Router = new VueRouter({
    scrollBehavior: () => ({ x: 0, y: 0 }),
    routes,

    // Leave these as is and change from quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    mode: process.env.VUE_ROUTER_MODE,
    base: process.env.VUE_ROUTER_BASE
  });

  Router.beforeEach(authGuard);

  return Router;
});
